import {
  Shareholding,
  ShareholdingType,
  useDeleteShareholding,
  useOwnership,
} from '@commenda/api';
import { friendlyOwnershipMessage } from '../forms/applyOwnershipApiError';
import {
  formatPersonList,
  previewInheritanceLoss,
} from '../previewInheritanceLoss';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@commenda/kakashi';

interface DeleteStakeholderConfirmProps {
  isOpen: boolean;
  onClose: () => void;
  corporationId: number;
  stakeholder?: Shareholding;
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
}

export const DeleteStakeholderConfirm = ({
  isOpen,
  onClose,
  corporationId,
  stakeholder,
  onSuccess,
  onError,
}: DeleteStakeholderConfirmProps) => {
  const mutation = useDeleteShareholding(corporationId);
  const { data: ownership } = useOwnership(corporationId);

  const lostUbos =
    stakeholder &&
    stakeholder.type === ShareholdingType.INSTITUTION &&
    !!stakeholder.stakeholderCorporationId
      ? previewInheritanceLoss(ownership, stakeholder.stakeholderCorporationId)
      : [];

  const handleConfirm = async () => {
    if (!stakeholder) return;
    try {
      await mutation.mutateAsync(stakeholder.stakeholdingId);
      onSuccess?.(`Removed ${stakeholder.name} from shareholders.`);
      onClose();
    } catch (err) {
      onError?.(friendlyOwnershipMessage(err));
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>
            {stakeholder ? `Remove ${stakeholder.name} as a shareholder?` : 'Remove shareholder?'}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-md p-3xl">
          {stakeholder ? (
            <>
              <p className="txt-small-regular text-text-secondary">
                Their stake will be removed from this corporation.
              </p>
              {lostUbos.length > 0 && stakeholder && (
                <p className="txt-small-regular text-text-secondary">
                  {formatPersonList(lostUbos.map((u) => u.name))}{' '}
                  {lostUbos.length === 1 ? 'is' : 'are'} listed as UBO
                  {lostUbos.length === 1 ? '' : 's'} of this entity only through{' '}
                  {stakeholder.name}. Removing this shareholder will also
                  remove {lostUbos.length === 1 ? 'them' : 'them'} as UBO
                  {lostUbos.length === 1 ? '' : 's'}.
                </p>
              )}
              <p className="txt-small-regular text-text-secondary">
                This can&apos;t be undone.
              </p>
            </>
          ) : (
            <p className="txt-small-regular text-text-secondary">
              This action cannot be undone.
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="secondary" size="md" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button
            variant="destructive-primary"
            size="md"
            type="button"
            onClick={handleConfirm}
            isLoading={mutation.isPending}
            loadingLabel="Removing…"
          >
            Remove
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
